import {
  Button, Card, Select, Space, Table, Typography,
} from 'antd';
import rentalApi from 'api/rentalApi';
import ToastHelper from 'general/helpers/ToastHelper';
import useRouter from 'hooks/useRouter';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  formatDateTime, formatMoney, getRentalErrorMessage, ORDER_STATUS_OPTIONS, OrderStatusTag,
} from '../../rentalConstants';

const { Text, Title } = Typography;

function RentalOrderListScreen() {
  const router = useRouter();
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await rentalApi.getOrders({ status: status || undefined, page, pageSize });
      setRows(response?.orders || []);
      setTotal(response?.total || 0);
    } catch (error) {
      setRows([]);
      setTotal(0);
      ToastHelper.showError(getRentalErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [status, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const goToDetail = (rentalOrderId) => router.navigate(`${rentalOrderId}`);

  const columns = useMemo(() => [
    { title: 'Mã đơn', dataIndex: 'orderCode', width: 140, render: (value) => <Text strong>{value}</Text> },
    {
      title: 'Khách thuê',
      key: 'customer',
      render: (_, row) => (
        <div>
          <div><Text strong>{row.customer?.fullname || 'Chưa có tên'}</Text></div>
          <Text type="secondary">{row.customer?.phone || `User #${row.customerId}`}</Text>
        </div>
      ),
    },
    {
      title: 'Xe',
      key: 'vehicle',
      render: (_, row) => `${row.vehicle?.vehicleCode || ''} · ${row.vehicle?.plate || ''}`,
    },
    { title: 'Bắt đầu', dataIndex: 'startAt', width: 160, render: formatDateTime },
    { title: 'Kết thúc', dataIndex: 'endAt', width: 160, render: formatDateTime },
    { title: 'Tổng tiền', dataIndex: 'totalFare', width: 130, render: formatMoney },
    { title: 'Trạng thái', dataIndex: 'status', width: 150, render: (value) => <OrderStatusTag status={value} /> },
    {
      title: '', key: 'action', width: 100,
      render: (_, row) => <Button type="link" onClick={() => goToDetail(row.rentalOrderId)}>Chi tiết</Button>,
    },
  ], []);

  return (
    <div className="container-fluid px-4 my-4">
      <Card>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
          <div>
            <Title level={4} className="mb-0">Đơn thuê xe</Title>
            <Text type="secondary">Tổng {total} đơn theo bộ lọc</Text>
          </div>
          <Button onClick={load} loading={loading}>Làm mới</Button>
        </div>
        <Space wrap className="mb-4">
          <Select
            value={status}
            options={ORDER_STATUS_OPTIONS}
            style={{ width: 200 }}
            onChange={(value) => { setStatus(value); setPage(1); }}
          />
        </Space>
        <Table
          rowKey="rentalOrderId"
          loading={loading}
          dataSource={rows}
          columns={columns}
          scroll={{ x: 1100 }}
          locale={{ emptyText: 'Không có dữ liệu' }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (nextPage, nextPageSize) => { setPage(nextPage); setPageSize(nextPageSize); },
          }}
          onRow={(row) => ({ onDoubleClick: () => goToDetail(row.rentalOrderId) })}
        />
      </Card>
    </div>
  );
}

export default RentalOrderListScreen;
